import { ColumnConfig } from '@blisspointmedia/bpm-types/dist/ColumnConfig';
import { ColumnOrMetricName, getConfig } from '@blisspointmedia/bpm-types/dist/Columns';
import {
  buildIconUrl,
  IconSpec,
  resolveIconTooltip
} from '@blisspointmedia/bpm-types/dist/dimensionIcons';
import { Formatter } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { DimensionColumn, PerformanceData } from '@blisspointmedia/bpm-types/dist/MetricsTable';
import {
  CDN,
  CellData,
  ColumnMetaData,
  ColumnMetaDataMap,
  DimensionMap
} from '@src/Components/MetricsTable/metrics-table-utils';

// Default `fetchKey` for callers that only have one bucket of metric data per
// row (e.g. global benchmarking). Multi-KPI consumers can pass their own.
const DEFAULT_FETCH_KEY = 'default';

// Generic shape that adapters expect — structurally compatible with whatever a
// page's resolveDataConfig / resolveDimConfig returns.
export interface DataConfigForMetricsTable {
  name: string;
  prettyName: string;
  formatter?: Formatter;
}

export interface DimensionConfigForMetricsTable extends Pick<ColumnConfig, 'name' | 'prettyName'> {
  iconStyle?: IconSpec['style'];
  formatter?: (raw: string | number | null) => string;
}

// Build a `ColumnMetaDataMap` (the `columnMetaDataMap` arg of `constructTableData`)
// from a list of resolved data configs. Each config's `name` becomes the map key
// — must match the corresponding `Column.dataVarName` so constructTableData can
// look up the metric's meta.
export const makeColumnMetaData = (dataConfigs: DataConfigForMetricsTable[]): ColumnMetaDataMap => {
  const map: Record<string, ColumnMetaData> = {};
  for (const cfg of dataConfigs) {
    map[cfg.name] = {
      prettyName: cfg.prettyName,
      formatter: cfg.formatter
    };
  }
  return map;
};

// Build a `ColumnMetaDataMap` keyed by dimension `prettyName` (matches
// `DimensionColumn.dimensionTypeName`). Carries `iconStyle` (from the icon spec) so
// MetricsTable can lay out the icon column for any dim with one set.
export const makeDimensionColumnMetaData = (
  dimensionConfigs: DimensionConfigForMetricsTable[]
): ColumnMetaDataMap => {
  const map: Record<string, ColumnMetaData> = {};
  for (const cfg of dimensionConfigs) {
    map[cfg.prettyName] = {
      prettyName: cfg.prettyName,
      ...(cfg.iconStyle ? { iconStyle: cfg.iconStyle } : {})
    };
  }
  return map;
};

// The `icon` spec bpm-types sets for this dim, if any.
export const getDimensionIcon = (dimName: string): IconSpec | undefined =>
  (getConfig(dimName as ColumnOrMetricName) as ColumnConfig | undefined)?.icon ?? undefined;

// Default `DimensionColumn.icon` for a dim's icon style: icon-only styles hide the label,
// every other style shows the icon next to it. Undefined when the dim has no icon.
export const defaultIconFor = (iconStyle?: IconSpec['style']): DimensionColumn['icon'] => {
  if (!iconStyle) return undefined;
  return iconStyle === 'iconOnly' ? 'iconOnly' : 'hasIcon';
};

// A dimension cell's icon `url` and hover text, from the `icon` spec bpm-types sets for this dim.
// `url` is undefined when the dim has no spec or the row lacks a key. `overlayText` is undefined
// when the spec sets no `tooltip` or the value is empty. `row` is the raw dimension row.
export const getDimensionIconCell = (
  dimName: string,
  row: Record<string, unknown>,
  company?: string
): Pick<CellData, 'url' | 'overlayText'> => {
  const icon = getDimensionIcon(dimName);
  if (!icon) return { url: undefined, overlayText: undefined };
  const path = buildIconUrl(icon, dimName, row, company);
  return {
    url: path ? `${CDN}/${path}` : undefined,
    overlayText: resolveIconTooltip(icon, dimName, row) ?? undefined
  };
};

// Build the `getDimensionCell` callback constructTableData expects, given the
// list of resolved dimension configs. Per-cell, the matching config's `formatter`
// is applied (e.g. `date` → human-readable label); otherwise the raw value is
// stringified. When the dim's `icon` spec yields a path, attaches a CDN `url` so
// MetricsTable can render the icon (it also requires the column to set
// `icon: 'hasIcon'` and the column meta to carry `iconStyle`).
export const makeDefaultGetDimensionCell = (
  dimensionConfigs: DimensionConfigForMetricsTable[]
): ((dims: DimensionMap, header: DimensionColumn) => CellData) => {
  const configsByName = new Map(dimensionConfigs.map(cfg => [cfg.name, cfg]));
  return (dims, header) => {
    const cfg = configsByName.get(header.dimensionVarName);
    const row = dims as Record<string, string | number | null>;
    const raw = row[header.dimensionVarName];
    const fallback = raw == null ? '' : String(raw);
    const label = cfg?.formatter ? cfg.formatter(raw) : fallback;
    const { url, overlayText } = cfg
      ? getDimensionIconCell(cfg.name, row)
      : { url: undefined, overlayText: undefined };
    return { label, value: label, overlayText, url };
  };
};

// Both `dimensions` and `fetches[fetchKey]` share a reference to the raw row —
// `makeGetDimensionCell` reads `dims[header.dimensionVarName]` and metric cells
// read `fetch[dataVarName]`, both of which the raw row already exposes.
export const flatRowsToPerformanceData = (
  rows: Record<string, string | number | null>[],
  fetchKey: string = DEFAULT_FETCH_KEY
): PerformanceData[] =>
  rows.map(
    row =>
      ({
        dimensions: row,
        fetches: { [fetchKey]: row }
      }) as unknown as PerformanceData
  );
